import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useRegisterMyPushDevice } from '@/service-library/hooks/notifications'
import queryKeys from '@/service-library/query-keys'
import {
  getPushMessaging,
  getPushToken,
  PUSH_TOKEN_STORAGE_KEY,
  trackPushRegistration,
} from '@/utils/push-notifications'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'

/** `unsupported` covers browsers without push and a missing VAPID key. */
export type PushPermission = NotificationPermission | 'unsupported'

/**
 * Keeps this browser registered for push and asks for permission on request.
 * Once permission is granted it registers the FCM token on every load and
 * account switch, so a rotated token replaces the old one and each account
 * the user opens here can reach them. `permission` is undefined while this
 * browser's support is still being checked.
 */
export default function usePushNotifications() {
  const queryClient = useQueryClient()
  const { currentAccount } = useCurrentAccountContext()
  const { registerMyPushDeviceAsync } = useRegisterMyPushDevice()
  const [permission, setPermission] = useState<PushPermission>()
  const [registeredToken, setRegisteredToken] = useState<string>()
  // Signing out reads this to remove the device. It's kept out of the
  // registration effect because its setter changes with every stored value.
  const [, setStoredPushToken] = useLocalStorage<string | null>(
    PUSH_TOKEN_STORAGE_KEY,
    null,
    false,
  )

  useEffect(() => {
    if (registeredToken) setStoredPushToken(registeredToken)
  }, [registeredToken, setStoredPushToken])

  useEffect(() => {
    let isCancelled = false
    getPushMessaging()
      .then((messagingInstance) => {
        if (isCancelled) return
        setPermission(
          messagingInstance ? Notification.permission : 'unsupported',
        )
      })
      .catch((error) => {
        console.error('Could not check push notification support', error)
        if (!isCancelled) setPermission('unsupported')
      })
    return () => {
      isCancelled = true
    }
  }, [])

  useEffect(() => {
    if (permission !== 'granted') return
    let isCancelled = false
    let unsubscribe: (() => void) | undefined

    async function register() {
      const token = await getPushToken()
      if (!token || isCancelled) return null
      await registerMyPushDeviceAsync({
        device_platform: 'GCM',
        device_token: token,
      })
      if (!isCancelled) setRegisteredToken(token)
      return token
    }

    // A push that arrives while this tab is focused comes here instead of to
    // the service worker, and nothing is shown. Refresh the bell instead.
    async function listenWhileFocused() {
      const messagingInstance = await getPushMessaging()
      const { onMessage } = await import('@firebase/messaging')
      if (!messagingInstance || isCancelled) return
      unsubscribe = onMessage(messagingInstance, () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all })
      })
    }

    trackPushRegistration(register()).catch((error) => {
      console.error('Could not register for push notifications', error)
    })
    listenWhileFocused().catch((error) => {
      console.error('Could not listen for push notifications', error)
    })

    return () => {
      isCancelled = true
      unsubscribe?.()
    }
  }, [currentAccount.id, permission, queryClient, registerMyPushDeviceAsync])

  /**
   * Shows the browser's own permission dialog, so only call it from a click
   * on the in-app prompt. Granting it registers this browser.
   */
  const enable = useCallback(async () => {
    setPermission(await Notification.requestPermission())
  }, [])

  return { permission, enable }
}
